import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Inbox, Layers, Plus, Columns3, ChevronDown, ChevronRight, ListChecks, MoreHorizontal, Users, Lock, CalendarDays, X, StickyNote,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { tasksAPI, taskListsAPI } from '@/lib/api';
import TaskPanel, { Checklist } from './TaskPanel';
import { GROUPS, groupFor, dueLabel, parseDue, dayToISO, ymd, initials } from './taskUtils';

const SEL_KEY = 'stagehead.tasks.sel';
const readSel = () => { try { return JSON.parse(localStorage.getItem(SEL_KEY)) || { kind: 'mine' }; } catch { return { kind: 'mine' }; } };
const PRI = { urgent: 'bg-[#A62B1F]', high: 'bg-amber-500' };
const PRI_RANK = { urgent: 0, high: 1, medium: 2, low: 3 };

// "Call George tomorrow" → due tomorrow. Only today/tomorrow at the very end, so titles aren't mangled.
const quickParse = (raw, day) => {
  const m = raw.match(/\s+(today|tomorrow|tmrw)$/i);
  if (!m) return { title: raw.trim(), day };
  const d = new Date();
  if (m[1].toLowerCase() !== 'today') d.setDate(d.getDate() + 1);
  return { title: raw.slice(0, m.index).trim(), day: ymd(d) };
};

const Row = ({ task, onToggle, onOpen, onChanged, showWhere, projects, lists }) => {
  const [open, setOpen] = useState(false);
  const due = parseDue(task.due_date);
  const g = groupFor(task);
  const proj = showWhere && task.project_id ? projects.find((p) => p._id === task.project_id) : null;
  const list = showWhere && task.list_id ? lists.find((l) => l.id === task.list_id) : null;
  const done = task.status === 'done';
  return (
    <div className="border-b border-[#F0EBE1] last:border-0">
      <div className="group flex items-center gap-2.5 px-3 py-2 hover:bg-[#FBF8F2]">
        <input type="checkbox" checked={done} onChange={() => onToggle(task)} className="h-4 w-4 accent-[#A62B1F] shrink-0" aria-label="Done" data-testid={`task-check-${task._id}`} />
        <button type="button" className="flex-1 min-w-0 text-left" onClick={() => onOpen(task)}>
          <span className={`text-sm ${done ? 'line-through text-[#94A3B8]' : 'text-[#1E2A33]'}`}>{task.title}</span>
          {task.note_id && <StickyNote className="inline w-3 h-3 ml-1 text-[#C9C0B0]" />}
        </button>
        {PRI[task.priority] && !done && <span className={`w-2 h-2 rounded-full ${PRI[task.priority]}`} title={task.priority} />}
        {(task.subtask_count > 0 || open) && (
          <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-0.5 text-[11px] text-[#5C6670] hover:text-[#1E2A33]">
            <ListChecks className="w-3.5 h-3.5" />{task.completed_subtasks || 0}/{task.subtask_count || 0}
          </button>
        )}
        {!task.subtask_count && !open && (
          <button type="button" onClick={() => setOpen(true)} className="opacity-0 group-hover:opacity-100 text-[11px] text-[#94A3B8] hover:text-[#1E2A33] flex items-center gap-0.5" title="Add a checklist">
            <ListChecks className="w-3.5 h-3.5" />
          </button>
        )}
        {proj && <span className="hidden sm:inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-[#E3DDD1] text-[#5C6670] max-w-[120px] truncate"><span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: proj.color || '#A62B1F' }} />{proj.name}</span>}
        {list && <span className="hidden sm:inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-[#F3EFE7] text-[#5C6670] max-w-[110px] truncate"><span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: list.color }} />{list.name}</span>}
        {due && <span className={`text-xs whitespace-nowrap ${!done && g === 'overdue' ? 'text-[#A62B1F] font-semibold' : !done && g === 'today' ? 'text-amber-700' : 'text-[#5C6670]'}`}>{dueLabel(task.due_date)}</span>}
        {task.assigned_to && <span title={task.assigned_to} className="w-6 h-6 rounded-full bg-[#1E2A33] text-white text-[10px] font-semibold flex items-center justify-center shrink-0">{initials(task.assigned_to)}</span>}
      </div>
      {open && (
        <div className="pl-10 pr-3 pb-2">
          <Checklist task={task} compact onCount={(n, d) => (n !== task.subtask_count || d !== task.completed_subtasks) && onChanged({ ...task, subtask_count: n, completed_subtasks: d })} />
        </div>
      )}
    </div>
  );
};

export default function TaskListView({ projects = [], roster = [], focusSignal = 0, onShowBoard }) {
  const [sel, setSelState] = useState(readSel);
  const [lists, setLists] = useState([]);
  const [tasks, setTasks] = useState(null);
  const [title, setTitle] = useState('');
  const [day, setDay] = useState('');
  const [showDone, setShowDone] = useState(false);
  const [panel, setPanel] = useState(null);
  const [newList, setNewList] = useState(null);
  const addRef = useRef(null);

  const setSel = (s) => { setSelState(s); try { localStorage.setItem(SEL_KEY, JSON.stringify(s)); } catch { /* private mode */ } };
  const loadLists = () => taskListsAPI.list().then((r) => setLists(r.data || [])).catch(() => setLists([]));
  useEffect(() => { loadLists(); }, []);
  useEffect(() => { if (focusSignal) addRef.current?.focus(); }, [focusSignal]);

  // If the saved list/project no longer exists, fall back to My tasks.
  useEffect(() => {
    if (sel.kind === 'project' && projects.length && !projects.some((p) => p._id === sel.id)) setSel({ kind: 'mine' });
  }, [projects]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setTasks(null);
    const params = sel.kind === 'mine' ? { mine: true } : sel.kind === 'list' ? { list_id: sel.id } : sel.kind === 'project' ? { project_id: sel.id } : {};
    tasksAPI.list(params).then((r) => setTasks(Array.isArray(r.data) ? r.data : [])).catch((e) => {
      if (sel.kind === 'list' && e?.response?.status === 404) { setSel({ kind: 'mine' }); return; }
      toast.error('Could not load tasks'); setTasks([]);
    });
  }, [sel.kind, sel.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const changed = (t) => setTasks((xs) => (xs || []).map((x) => (x._id === t._id ? t : x)));
  const removed = (id) => setTasks((xs) => (xs || []).filter((x) => x._id !== id));
  const toggle = async (t) => {
    const status = t.status === 'done' ? 'backlog' : 'done';
    changed({ ...t, status, completed_at: status === 'done' ? new Date().toISOString() : null });
    try { await tasksAPI.update(t._id, { status }); if (sel.kind === 'list') loadLists(); }
    catch { changed(t); toast.error('Could not update the task'); }
  };

  const add = async (e) => {
    e?.preventDefault();
    if (!title.trim()) return;
    const q = quickParse(title, day);
    if (!q.title) return;
    const payload = { title: q.title, due_date: dayToISO(q.day), status: 'backlog',
      project_id: sel.kind === 'project' ? sel.id : null, list_id: sel.kind === 'list' ? sel.id : null };
    setTitle('');
    try {
      const r = await tasksAPI.create(payload);
      setTasks((xs) => [{ ...r.data, subtask_count: 0, completed_subtasks: 0 }, ...(xs || [])]);
      if (sel.kind === 'list') loadLists();
    } catch (err) { setTitle(title); toast.error(err?.response?.data?.detail || 'Could not add the task'); }
  };

  const createList = async () => {
    const name = (newList || '').trim();
    setNewList(null);
    if (!name) return;
    try { const r = await taskListsAPI.create({ name }); setLists((l) => [...l, r.data]); setSel({ kind: 'list', id: r.data.id }); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not make the list'); }
  };
  const currentList = sel.kind === 'list' ? lists.find((l) => l.id === sel.id) : null;
  const renameList = async () => {
    const name = window.prompt('Rename list', currentList.name);
    if (!name || !name.trim()) return;
    try { await taskListsAPI.update(currentList.id, { name }); loadLists(); } catch (e) { toast.error(e?.response?.data?.detail || 'Could not rename'); }
  };
  const shareList = async () => {
    try { await taskListsAPI.update(currentList.id, { shared: !currentList.shared }); loadLists(); toast.success(currentList.shared ? 'Only you can see this list now' : 'Your team can see this list now'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not change that'); }
  };
  const deleteList = async () => {
    if (!window.confirm(`Delete the list "${currentList.name}"? Its tasks are kept, just not in a list.`)) return;
    try { await taskListsAPI.remove(currentList.id); setSel({ kind: 'mine' }); loadLists(); } catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete it'); }
  };

  const { groups, done } = useMemo(() => {
    const open = (tasks || []).filter((t) => t.status !== 'done');
    const sortKey = (t) => [parseDue(t.due_date)?.getTime() ?? Infinity, PRI_RANK[t.priority] ?? 2, -(parseDue(t.created_at)?.getTime() || 0)];
    const cmp = (a, b) => { const x = sortKey(a), y = sortKey(b); for (let i = 0; i < x.length; i += 1) if (x[i] !== y[i]) return x[i] < y[i] ? -1 : 1; return 0; };
    return {
      groups: GROUPS.map((g) => ({ ...g, tasks: open.filter((t) => groupFor(t) === g.id).sort(cmp) })).filter((g) => g.tasks.length),
      done: (tasks || []).filter((t) => t.status === 'done').sort((a, b) => (parseDue(b.completed_at || b.updated_at)?.getTime() || 0) - (parseDue(a.completed_at || a.updated_at)?.getTime() || 0)),
    };
  }, [tasks]);

  const heading = sel.kind === 'mine' ? 'My tasks' : sel.kind === 'all' ? 'Everyone\'s tasks'
    : sel.kind === 'list' ? (currentList?.name || 'List') : (projects.find((p) => p._id === sel.id)?.name || 'Project');
  const showWhere = sel.kind === 'mine' || sel.kind === 'all';
  const railBtn = (active) => `w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm text-left whitespace-nowrap ${active ? 'bg-[#1E2A33] text-white' : 'text-[#33404A] hover:bg-[#F3EFE7]'}`;

  return (
    <div className="flex flex-col md:flex-row gap-4">
      {/* Rail: who / lists / projects */}
      <aside className="md:w-56 shrink-0">
        <div className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible pb-1">
          <button className={railBtn(sel.kind === 'mine')} onClick={() => setSel({ kind: 'mine' })}><Inbox className="w-4 h-4" />My tasks</button>
          <button className={railBtn(sel.kind === 'all')} onClick={() => setSel({ kind: 'all' })}><Layers className="w-4 h-4" />Everyone's</button>
          <div className="hidden md:block text-[11px] uppercase tracking-wider font-semibold text-[#94A3B8] px-2.5 pt-3 pb-1">Lists</div>
          {lists.map((l) => (
            <button key={l.id} className={railBtn(sel.kind === 'list' && sel.id === l.id)} onClick={() => setSel({ kind: 'list', id: l.id })}>
              <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: l.color }} /><span className="flex-1 truncate">{l.name}</span>
              {l.shared ? <Users className="w-3 h-3 opacity-60" /> : null}{l.open > 0 && <span className="text-[11px] opacity-70">{l.open}</span>}
            </button>
          ))}
          {newList !== null ? (
            <input autoFocus value={newList} onChange={(e) => setNewList(e.target.value)} onBlur={createList}
              onKeyDown={(e) => { if (e.key === 'Enter') createList(); if (e.key === 'Escape') setNewList(null); }}
              placeholder="List name, Enter" className="px-2.5 py-1.5 text-sm rounded-md border border-[#E3DDD1] outline-none focus:border-[#1E2A33] min-w-[140px]" />
          ) : (
            <button className={`${railBtn(false)} text-[#5C6670]`} onClick={() => setNewList('')}><Plus className="w-4 h-4" />New list</button>
          )}
          {projects.length > 0 && <div className="hidden md:block text-[11px] uppercase tracking-wider font-semibold text-[#94A3B8] px-2.5 pt-3 pb-1">Projects</div>}
          {projects.map((p) => (
            <button key={p._id} className={railBtn(sel.kind === 'project' && sel.id === p._id)} onClick={() => setSel({ kind: 'project', id: p._id })}>
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: p.color || '#A62B1F' }} /><span className="flex-1 truncate">{p.name}</span>
            </button>
          ))}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-xl font-semibold text-[#1E2A33] mr-auto truncate" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>{heading}</h2>
          {currentList && (
            <span className="text-xs text-[#5C6670] flex items-center gap-1">{currentList.shared ? <><Users className="w-3 h-3" />Shared with team</> : <><Lock className="w-3 h-3" />Only you</>}</span>
          )}
          {currentList && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={renameList}>Rename</DropdownMenuItem>
                {currentList.mine && <DropdownMenuItem onClick={shareList}>{currentList.shared ? 'Make private' : 'Share with team'}</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-red-600" onClick={deleteList}>Delete list</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {sel.kind === 'project' && <Link to={`/projects/${sel.id}`} className="text-xs font-semibold text-[#A62B1F]">Project page</Link>}
          <Button variant="outline" size="sm" onClick={onShowBoard}><Columns3 className="w-4 h-4 mr-1" />Board</Button>
        </div>

        <form onSubmit={add} className="flex items-center gap-2 rounded-xl border border-[#E3DDD1] bg-white px-3 py-2 focus-within:border-[#1E2A33]">
          <Plus className="w-4 h-4 text-[#A62B1F]" />
          <input ref={addRef} value={title} onChange={(e) => setTitle(e.target.value)} data-testid="task-quick-add"
            placeholder={`Add a task${sel.kind === 'list' ? ` to ${heading}` : sel.kind === 'project' ? ` to ${heading}` : ''}, press Enter (try "… tomorrow")`}
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-[#B5AC9C]" />
          <span className={`relative flex items-center gap-1 text-xs cursor-pointer rounded-md px-2 py-1 ${day ? 'bg-[#F3EFE7] text-[#1E2A33]' : 'text-[#94A3B8] hover:text-[#1E2A33]'}`} title="Due date">
            <CalendarDays className="w-4 h-4" />{day ? dueLabel(dayToISO(day)) : ''}
            {/* Invisible date input laid over the icon: tapping it opens the phone/desktop date picker */}
            <input type="date" value={day} onChange={(e) => setDay(e.target.value)} aria-label="Due date"
              className="absolute inset-0 opacity-0 cursor-pointer w-full" onClick={(e) => { try { e.currentTarget.showPicker(); } catch { /* older browsers open on focus */ } }} />
          </span>
          {day && <button type="button" onClick={() => setDay('')} className="text-[#94A3B8]" aria-label="Clear date"><X className="w-3.5 h-3.5" /></button>}
        </form>

        <div className="mt-4 space-y-4">
          {tasks === null ? <div className="text-sm text-[#5C6670] py-6">Loading…</div> : (
            <>
              {groups.length === 0 && (
                <div className="rounded-xl border border-dashed border-[#E3DDD1] py-10 text-center text-sm text-[#5C6670]">
                  {done.length ? 'All done here. Nice.' : 'Nothing here yet. Type above and press Enter.'}
                </div>
              )}
              {groups.map((g) => (
                <div key={g.id}>
                  <div className={`text-xs font-semibold uppercase tracking-wider mb-1 ${g.id === 'overdue' ? 'text-[#A62B1F]' : 'text-[#5C6670]'}`}>{g.label} <span className="font-normal">{g.tasks.length}</span></div>
                  <div className="rounded-xl border border-[#E3DDD1] bg-white overflow-hidden">
                    {g.tasks.map((t) => <Row key={t._id} task={t} onToggle={toggle} onOpen={setPanel} onChanged={changed} showWhere={showWhere} projects={projects} lists={lists} />)}
                  </div>
                </div>
              ))}
              {done.length > 0 && (
                <div>
                  <button className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8] flex items-center gap-1 mb-1" onClick={() => setShowDone(!showDone)}>
                    {showDone ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}Done <span className="font-normal">{done.length}</span>
                  </button>
                  {showDone && (
                    <div className="rounded-xl border border-[#E3DDD1] bg-white overflow-hidden opacity-80">
                      {done.slice(0, 50).map((t) => <Row key={t._id} task={t} onToggle={toggle} onOpen={setPanel} onChanged={changed} showWhere={showWhere} projects={projects} lists={lists} />)}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <TaskPanel task={panel && (tasks || []).find((t) => t._id === panel._id)} open={!!panel} onOpenChange={(o) => !o && setPanel(null)}
        onChanged={changed} onDeleted={(id) => { removed(id); setPanel(null); }} projects={projects} lists={lists} roster={roster} />
    </div>
  );
}
